import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";

import { Project } from "types";
import { ImageType, SpecialUploadType } from "components/Documents/types";

interface ProjectContextPanelProps {
  projectId: string;
  project: Project;
}

// Read-only disclosure of exactly WHAT project data is sent to the AI
// (plan §1.2 item 10). The server gathers this baseline itself (assumption 7),
// so there are no include-toggles here — the panel only makes the inputs
// visible. Collapsible to keep the start view compact.
export const ProjectContextPanel = ({
  projectId,
  project,
}: ProjectContextPanelProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  const { boundary, directives, documents, projectdata, standards } = project;

  const mainProductImage = documents?.find(
    (document) => document.type === ImageType.mainProductImage
  );
  const detailedDescription = documents?.find(
    (document) => document.type === SpecialUploadType.detailedDescription
  );

  // i18n quirk respected (codebase_map.md): boundary.width is labeled
  // "length" (form.boundary.width) and boundary.depth "height"
  // (form.boundary.height) — same labels the boundaries editor shows.
  const dimensionRows = [
    { labelKey: "form.boundary.weight", value: boundary?.weight || "" },
    { labelKey: "form.boundary.width", value: boundary?.width || "" },
    { labelKey: "form.boundary.height", value: boundary?.depth || "" },
  ];
  const textRows = [
    {
      labelKey: "form.boundary.expected_usage",
      value: boundary?.expected_usage || "",
    },
    {
      labelKey: "form.boundary.expected_misuse",
      value: boundary?.expected_misuse || "",
    },
  ];

  const additionalBoundaries = (boundary?.additional_boundaries || []).filter(
    (additional) => additional.description || additional.value
  );

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-cyan-500 rounded-lg"
      >
        <span>
          <span className="block text-sm font-medium text-gray-900">
            {t("risks.ai.start.context.headline")}
          </span>
          <span className="block text-xs text-gray-500">
            {t("risks.ai.start.context.hint")}
          </span>
        </span>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        ) : (
          <ChevronDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-200 divide-y divide-gray-100">
          <div className="py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {t("risks.ai.start.context.descriptionLabel")}
            </p>
            <p className="text-sm font-medium text-gray-900">
              {projectdata.product_name}
            </p>
            {projectdata.description && (
              <p className="text-sm text-gray-700 whitespace-pre-line">
                {projectdata.description}
              </p>
            )}
          </div>

          <div className="py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {t("risks.ai.start.context.imageLabel")}
            </p>
            {mainProductImage ? (
              <img
                src={mainProductImage.location}
                alt={t("risks.ai.start.context.imageLabel")}
                className="h-24 rounded border border-gray-200 object-contain"
              />
            ) : (
              <p className="text-sm text-gray-500">
                {t("risks.ai.start.context.noImage")}
              </p>
            )}
          </div>

          <div className="py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t("risks.ai.start.context.boundaryLabel")}
            </p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {dimensionRows.map(({ labelKey, value }) => (
                <div key={labelKey}>
                  <p className="text-xs text-gray-400">{t(labelKey)}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {value || "—"}
                  </p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {textRows.map(({ labelKey, value }) => (
                <div key={labelKey}>
                  <p className="text-xs text-gray-400">{t(labelKey)}</p>
                  <p className="text-sm text-gray-700">{value || "—"}</p>
                </div>
              ))}
              {additionalBoundaries.map((additional, index) => (
                <div key={`${additional.description}-${index}`}>
                  <p className="text-xs text-gray-400">
                    {additional.description}
                  </p>
                  <p className="text-sm text-gray-700">
                    {additional.value || "—"}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {t("risks.ai.start.context.detailedDescriptionLabel")}
            </p>
            {detailedDescription ? (
              <CheckIcon className="h-4 w-4 text-green-500" aria-hidden="true" />
            ) : (
              <p className="text-sm text-gray-500">
                {t("risks.ai.start.context.detailedDescriptionMissing")}
              </p>
            )}
          </div>

          <div className="py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {t("risks.ai.start.context.directivesLabel")}
            </p>
            {directives?.length ? (
              <div className="flex flex-wrap gap-2">
                {directives.map((directive) => (
                  <span
                    key={directive.name}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {t(`directives.enum.${directive.name}`, {
                      defaultValue: directive.name,
                    })}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">—</p>
            )}
          </div>

          <div className="py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {t("risks.ai.start.context.standardsLabel")}
            </p>
            <p className="text-sm text-gray-900">{standards?.length || 0}</p>
          </div>

          <div className="py-3">
            <p className="text-sm text-gray-500">
              {t("risks.ai.start.context.editHint")}{" "}
              <Link
                to={`/projects/${projectId}/edit`}
                className="text-cyan-600 hover:text-cyan-700"
              >
                {t("machineDetails")}
              </Link>
              {" · "}
              <Link
                to={`/projects/${projectId}/edit/boundaries`}
                className="text-cyan-600 hover:text-cyan-700"
              >
                {t("form.boundary.label")}
              </Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
